/**
 * @fileoverview Service for managing document access control
 */
class DocumentAccessService {
    storageKey = 'certiChain_document_access';
    /**
     * Get all access records for a document
     */
    getDocumentAccesses(documentId) {
        try {
            const allAccesses = this.getAllAccesses();
            return allAccesses.filter(access => access.documentId === documentId && access.isActive);
        }
        catch (error) {
            console.error('Error getting document accesses:', error);
            return [];
        }
    }
    /**
     * Get all documents a user has access to
     */
    getUserAccesses(userEmail) {
        try {
            const allAccesses = this.getAllAccesses();
            return allAccesses.filter(access => access.grantedTo.toLowerCase() === userEmail.toLowerCase() &&
                access.isActive &&
                (!access.expiresAt || new Date(access.expiresAt) > new Date()));
        }
        catch (error) {
            console.error('Error getting user accesses:', error);
            return [];
        }
    }
    /**
     * Grant access to a document
     */
    grantAccess(request, grantedBy) {
        try {
            const access = {
                id: this.generateId(),
                documentId: request.documentId,
                grantedTo: request.email.toLowerCase(),
                grantedToName: request.name,
                permission: request.permission,
                grantedBy,
                grantedAt: new Date(),
                expiresAt: request.expiresAt,
                isActive: true,
            };
            const allAccesses = this.getAllAccesses();
            allAccesses.push(access);
            localStorage.setItem(this.storageKey, JSON.stringify(allAccesses));
            return access;
        }
        catch (error) {
            console.error('Error granting access:', error);
            throw error;
        }
    }
    /**
     * Revoke access to a document
     */
    revokeAccess(accessId) {
        try {
            const allAccesses = this.getAllAccesses();
            const access = allAccesses.find(a => a.id === accessId);
            if (access) {
                access.isActive = false;
                localStorage.setItem(this.storageKey, JSON.stringify(allAccesses));
            }
        }
        catch (error) {
            console.error('Error revoking access:', error);
            throw error;
        }
    }
    /**
     * Check if user has access to document
     */
    hasAccess(documentId, userEmail) {
        try {
            const accesses = this.getDocumentAccesses(documentId);
            return accesses.some(access => access.grantedTo.toLowerCase() === userEmail.toLowerCase() &&
                (!access.expiresAt || new Date(access.expiresAt) > new Date()));
        }
        catch (error) {
            console.error('Error checking access:', error);
            return false;
        }
    }
    /**
     * Get all accessible documents for a recipient (both owned and granted access)
     */
    getAccessibleDocumentsForRecipient(userEmail) {
        try {
            const userAccesses = this.getUserAccesses(userEmail);
            if (userAccesses.length === 0) {
                return [];
            }
            // Import documentService dynamically to avoid circular dependencies
            const { documentService } = require('./documentService');
            const accessDocIds = userAccesses.map(a => a.documentId);
            const accessDocs = accessDocIds
                .map(id => documentService.getDocumentById(id))
                .filter(d => d !== null);
            return accessDocs;
        }
        catch (error) {
            console.error('Error getting accessible documents:', error);
            return [];
        }
    }
    /**
     * Get all accesses from storage
     */
    getAllAccesses() {
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (!stored)
                return [];
            return JSON.parse(stored).map((access) => ({
                ...access,
                grantedAt: new Date(access.grantedAt),
                expiresAt: access.expiresAt ? new Date(access.expiresAt) : undefined,
            }));
        }
        catch (error) {
            console.error('Error parsing accesses from storage:', error);
            return [];
        }
    }
    /**
     * Generate unique ID
     */
    generateId() {
        return `access_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
}
export const documentAccessService = new DocumentAccessService();
